import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import usePharmacyQuery from '@/hooks/pharmacy/usePharmacyQuery';
import { getErrorMessage } from '@/utils/error';
import { sendPrintMessage } from '@/utils/zebraBrowserPrinter';
import { localeMoment } from '@/utils/time';
import { printVroongShippingLabelwithZPL } from '@/utils/zebraBrowserPrinter/printVroongShippingLabelwithZPL';
import GlobalAlert from '@/components/GlobalAlert';
import PrintDoobalHeroShippingLabel from '@/components/PrintDoobalHeroShippingLabel';
import TextComponent from '@/components/TextComponent';
import Tooltip from '@/components/Tooltip';
import { DoobalHeroShippingLabelResponse } from '@/apis/pharmacy/interface';
import PrintTeamFreshShippingLabel from '@/components/PrintTeamFreshShippingLabel';
import pharmacyServices, {
  PRINT_SHIPPING_LABEL_BUTTON_TYPE,
  PrintShippingLabelButtonType,
  TEAM_FRESH_DAWN_TYPES
} from '@/services/phamacy';
import { ShippingLabelByPharmacistService } from '@/services/phamacy/interface';
import theme from '@/styles/theme';

interface Props {
  type: PrintShippingLabelButtonType;
  orderId: number;
  orderNumber?: string;
}

interface ReprintButtonContainerProps {
  onClick: () => void;
  addShipping?: boolean;
}

const ReprintButtonContainer: FunctionComponent<
  ReprintButtonContainerProps
> = props => {
  return (
    <div>
      <ReprintButton {...props} data-for="reprintInvoiceButton" data-tip>
        송장 재출력
      </ReprintButton>
      <Tooltip
        id="reprintInvoiceButton"
        place="bottom"
        type="info"
        effect="solid">
        <TextComponent color={'#fff'}>
          {`출력 중 에러가 발생한 경우, 위 버튼을 눌러
                  송장을 다시 출력할 수 있습니다.`}
        </TextComponent>
      </Tooltip>
    </div>
  );
};

const PrintShippingLabelButton: FunctionComponent<Props> = ({
  type,
  orderId,
  orderNumber
}) => {
  const [doobalHeroShippingLabelInfo, setDoobalHeroShippingLabelInfo] =
    useState<DoobalHeroShippingLabelResponse | null>(null);

  const [teamFreshShippingLabelInfo, setTeamFreshShippingLabelInfo] =
    useState<ShippingLabelByPharmacistService | null>(null);
  const {
    requestVroongShippingLabelMutation,
    requestDoobalHeroShippingLabelMutation,
    requestTeamFreshShippingLabelMutation,
    requestTeamFreshDeliveryReceiptMutation
  } = usePharmacyQuery();

  const closeDoobalHeroShippingLabelHandle = () => {
    setDoobalHeroShippingLabelInfo(null);
  };

  const closeTeamFreshShippingLabelHandle = () => {
    setTeamFreshShippingLabelInfo(null);
  };

  const getShippingLabelErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      let errorMessage =
        '일시적 장애가 발생하여 송장 출력이 불가능합니다. 현재 긴급 대응 중이며 빠른 정상화를 위해 노력하고 있습니다. 불편을 드려 죄송합니다.';
      if (error?.response?.data?.code) {
        switch (error.response.data.code) {
          // 부릉 송장 출력 가능 시간이 아님
          case 'VroongShippingLabelValidTimeException':
            const isWeekend = [0, 6].includes(localeMoment().day());
            errorMessage = `새벽 배송 주문이 마감되어 송장 출력이 불가능합니다.
              ${
                isWeekend ? '17' : '20'
              }시 이후 결제 완료 건은 내일 09시 이후 출력 부탁 드립니다.`;
            break;
        }
      }
      return errorMessage;
    } else {
      return getErrorMessage(error);
    }
  };

  const requestVroongShippingLabel = async () => {
    const addShipping =
      type === PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN_ADD_SHIPPING;

    try {
      const result = await requestVroongShippingLabelMutation.mutateAsync({
        orderId,
        addShipping
      });
      const shippingLabelZPL = printVroongShippingLabelwithZPL(result);
      await sendPrintMessage(shippingLabelZPL);
    } catch (error) {
      GlobalAlert.show({
        title: getShippingLabelErrorMessage(error)
      });
    }
  };

  const requestDoobalHeroShippingLabel = async () => {
    try {
      const result = await requestDoobalHeroShippingLabelMutation.mutateAsync(
        orderId
      );
      setDoobalHeroShippingLabelInfo(result);
    } catch (error) {
      setDoobalHeroShippingLabelInfo(null);
      GlobalAlert.show({
        title: getShippingLabelErrorMessage(error)
      });
    }
  };

  const requestTeamFreshShippingLabel =
    (deliveryType: TEAM_FRESH_DAWN_TYPES) => async () => {
      if (!orderNumber) return;

      //FIXME: BFF 타입 수정 이후에 객체형식으로 공용함수 실행하는 부분이 타입을 제대로 읽지못하여 객체형식에서 switch로 변경하였습니다.
      switch (deliveryType) {
        case 'TEAM_FRESH_DAWN': {
          const res = await requestTeamFreshDeliveryReceiptMutation.mutateAsync(
            orderNumber
          );

          const teamFreshShippingLabelInfoService =
            pharmacyServices.getTeamFreshDeliveryReceiptService(res);

          setTeamFreshShippingLabelInfo(teamFreshShippingLabelInfoService);
          return;
        }
        case 'TEAM_FRESH_DAWN_REPRINT': {
          const res = await requestTeamFreshShippingLabelMutation.mutateAsync(
            orderNumber
          );

          const teamFreshShippingLabelInfoService =
            pharmacyServices.getTeamFreshShipLabelService(res);

          setTeamFreshShippingLabelInfo(teamFreshShippingLabelInfoService);
          return;
        }
      }
    };

  switch (type) {
    // 부릉 - 새벽 배송
    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN:
      return (
        <PrintButton onClick={requestVroongShippingLabel}>
          약 배달 준비 완료
        </PrintButton>
      );
    // 부릉 - 새벽 배송 재출력
    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN_REPRINT:
      return <ReprintButtonContainer onClick={requestVroongShippingLabel} />;
    // 부릉 - 새벽 배송 추가 출고
    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN_ADD_SHIPPING:
      return (
        <ReprintButton addShipping onClick={requestVroongShippingLabel}>
          추가 출고
        </ReprintButton>
      );
    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.DOOBAL_HERO_DELIVERY:
      return (
        <>
          <PrintButton onClick={requestDoobalHeroShippingLabel}>
            약 배달 준비 완료
          </PrintButton>
          {doobalHeroShippingLabelInfo && (
            <PrintDoobalHeroShippingLabel
              data={doobalHeroShippingLabelInfo}
              closeDoobalHeroShippingLabelHandle={
                closeDoobalHeroShippingLabelHandle
              }
            />
          )}
        </>
      );
    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.DOOBAL_HERO_DELIVERY_REPRINT:
      return (
        <>
          <ReprintButtonContainer onClick={requestDoobalHeroShippingLabel} />
          {doobalHeroShippingLabelInfo && (
            <PrintDoobalHeroShippingLabel
              data={doobalHeroShippingLabelInfo}
              closeDoobalHeroShippingLabelHandle={
                closeDoobalHeroShippingLabelHandle
              }
            />
          )}
        </>
      );

    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN:
      return (
        <>
          <PrintButton
            onClick={requestTeamFreshShippingLabel(
              PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN
            )}>
            약 배달 준비 완료
          </PrintButton>
          {teamFreshShippingLabelInfo && (
            <PrintTeamFreshShippingLabel
              data={teamFreshShippingLabelInfo}
              closeTeamFreshShippingLabelHandle={
                closeTeamFreshShippingLabelHandle
              }
            />
          )}
        </>
      );
    case PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN_REPRINT:
      return (
        <>
          <ReprintButtonContainer
            onClick={requestTeamFreshShippingLabel(
              PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN_REPRINT
            )}
          />
          {teamFreshShippingLabelInfo && (
            <PrintTeamFreshShippingLabel
              data={teamFreshShippingLabelInfo}
              closeTeamFreshShippingLabelHandle={
                closeTeamFreshShippingLabelHandle
              }
            />
          )}
        </>
      );
    default:
      return <></>;
  }
};

export default PrintShippingLabelButton;

const Button = styled.button`
  display: flex;
  align-content: center;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }

  :disabled {
    opacity: 0.5;
  }
`;

const PrintButton = styled(Button)`
  margin-right: 20px;
  padding: 9px 26px;
  border: ${theme.DF_COLOR.GREEN500};
  border-radius: 5px;
  background: ${theme.DF_COLOR.GREEN500};
  color: ${theme.DF_COLOR.WHITE};
`;

const ReprintButton = styled(Button)<{ addShipping?: boolean }>`
  ${({ addShipping }) => !addShipping && 'margin-top: 35px'};
  padding: 10px 14px;
  border: 1px solid ${theme.DF_COLOR.GRAY600};
  border-radius: 5px;
  font-weight: 500;
`;
